import { CheckCircleFilled, LinkOutlined, RedoOutlined, UserAddOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Col, Modal, Row, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { produce } from 'immer';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAuth } from '../../../contexts/authentication.context';
import { Role } from '../../../enums/role';
import { useMedia } from '../../../hooks';
import authService from '../../../services/auth.service';
import candidatesService from '../../../services/candidates.service';
import jobsService from '../../../services/jobs.service';
import { JobApplicationState } from '../../../types/job-application';
import { getCandidateTypeFromCode } from '../../../utils';
import { timeAgo } from '../../../utils/date';
import getFormattedCurrentSalary from '../../../utils/formatted-currentSalary';
import { getFormattedSalary } from '../../../utils/formatted-salary';
import { getNoticePeriod } from '../../../utils/notice-period';
import { notifyError, notifySuccess } from '../../../utils/notification';
import { titlize } from '../../../utils/strings';
import { CommentBox } from '../../comments/comment-box';
import { CommentItem } from '../../comments/comment-item';
import { ComponentGuard } from '../../component-guard';
import { PDFViewer } from '../../pdf-viewer-modal/pdf-viewer-modal';
import { SkeletonList } from '../../skeleton-list/skeleton-list';
import { ButtonType, TAButton } from '../../widgets';
import ApplicationStateChangeLogViewModal from './application-state-changelog-view-modal';
import CandidateApplicationHistoryViewModal from './candidate-applications-history-view-modal';
import { CandidateAvatar } from './candidate-avatar';
import { HighlightedCandidateCardInfo } from './highlighted-candidate-card-info';

const InvitationStatusColorMap = {
	pending: 'geekblue',
	accepted: 'green',
	expired: 'volcano'
};
export default function CandidateViewModal(props) {
	const {
		candidate,
		application,
		showCustomReports = true,
		job,
		showComments = false,
		commentsSort = 'asc',
		showStateChangeLog = false,
		showApplicationsHistory = false,
		showAvatar
	} = props;
	const { user } = useAuth();
	const [comments, setComments] = useState<any>();
	const [commentsLoading, setCommentsLoading] = useState(false);
	const [newCommentLoading, setNewCommentLoading] = useState(false);
	const [changeLogModalVisible, setChangeLogModalVisible] = useState(false);
	const [applicationsModalVisible, setApplicationsModalVisible] = useState(false);
	const [commentFromParam, setCommentFromParam] = useState<string | null>(null);
	const location = useLocation();

	const theme = useTheme();
	const commentsContainerRef = useRef<any>();
	const commentRef = useRef<any>();
	const userAvatarMap = useMemo(() => ({}), []);

	const [pdfViewerState, setPdfViewerState] = useState<any>({
		visible: false,
		url: '',
		title: '',
		downloadable: false
	});
	const [applications, setApplications] = useState([]);

	useEffect(() => {
		if (showApplicationsHistory) {
			candidatesService.getCandidateApplications(candidate.id).then((res: any) => setApplications(res));
		}
	}, [candidate.id, showApplicationsHistory]);

	//TODO: Refactor this logic to a custom hook or HoC
	const [copied, setCopied] = useState(false);
	const [invitationSent, setInvitationSent] = useState(false);
	useEffect(() => {
		if (copied) {
			setTimeout(() => setCopied(false), 2000);
		}
	}, [copied]);
	useEffect(() => {
		if (invitationSent) {
			setTimeout(() => setInvitationSent(false), 2000);
		}
	}, [invitationSent]);

	useEffect(() => {
		if (showComments && job) {
			setCommentsLoading(true);
			jobsService.getApplicantComments(job._id, application.id).then(comments => {
				setComments(
					comments.map(c => {
						if (c.createdBy._id === user._id) {
							c.createdBy.avatarBgColor = user.avatarBgColor;
						} else {
							if (userAvatarMap[c.createdBy._id])
								c.createdBy.avatarBgColor = userAvatarMap[c.createdBy._id];
							else {
								userAvatarMap[c.createdBy._id] = c.createdBy.avatarBgColor = '#4ADCAA';
							}
						}
						return c;
					})
				);
				setCommentsLoading(false);
			});
		}
	}, [application, showComments, job, commentsSort, candidate, user, userAvatarMap]);

	useEffect(() => {
		const commentId = new URLSearchParams(location.search).get('comment');
		setCommentFromParam(commentId);
	}, [location]);

	useEffect(() => {
		if (commentRef.current) {
			commentRef.current.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';

			commentsContainerRef.current.scrollTo({ top: commentRef.current.offsetTop - 300, behavior: 'smooth' });
		}
	});

	const handleAddComment = (comment, attachment, callback) => {
		setNewCommentLoading(true);
		jobsService.commentOnApplicantCard(job._id, application.id, comment, attachment).then(newComment => {
			const commentsList: any[] = [
				{
					...newComment,
					createdBy: {
						...user,
						id: user.userId
					}
				},
				...comments
			];
			setComments(commentsList);
			callback();
		});
	};

	const renderCvLastUpdate = lastCvUpdate => {
		if (props.minimal) return null;
		else
			return (
				<span style={{ color: 'rgba(0,0,0,0.35)', display: 'block' }}>
					<i>CV Last updated: {dayjs(lastCvUpdate).format('MMMM D, YYYY')}</i>
				</span>
			);
	};
	// const onAddApplicantReportSuccess = response => {
	// 	candidate.technicalReport = response.reportUrl;
	// 	setAddReportModalVisible(false);
	// 	notifySuccess('Report added Successfully');
	// };

	const renderSalaryAndNotice = (application, candidate) => {
		return (
			<>
				{application ? (
					<>
						{application.currentSalary?.value > 0 ? (
							<ComponentGuard
								allowed={authService.hasRoles(
									Role.BACKOFFICE_ADMIN,
									Role.BACKOFFICE_USER,
									Role.COMPANY_ADMIN,
									Role.COMPANY_OWNER
								)}
							>
								<HighlightedCandidateCardInfo
									title="Current Salary"
									value={getFormattedCurrentSalary(application.currentSalary)}
								/>
							</ComponentGuard>
						) : null}
						<ComponentGuard
							allowed={authService.hasRoles(
								Role.BACKOFFICE_SUPER_ADMIN,
								Role.BACKOFFICE_ADMIN,
								Role.BACKOFFICE_USER,
								Role.COMPANY_ADMIN,
								Role.COMPANY_OWNER
							)}
						>
							<HighlightedCandidateCardInfo
								title="Application Expected Salary"
								value={
									application.expectedSalary?.min > 0
										? getFormattedSalary(application.expectedSalary)
										: 'Negotiable'
								}
							/>
						</ComponentGuard>
						{application.noticePeriod ? (
							<HighlightedCandidateCardInfo
								title="Application Notice Period"
								value={getNoticePeriod(application.noticePeriod)}
							/>
						) : null}
					</>
				) : null}
				{!application && candidate ? (
					<>
						{candidate.currentSalary?.value > 0 ? (
							<HighlightedCandidateCardInfo
								title="Candidate Current Salary"
								value={getFormattedCurrentSalary(candidate.currentSalary)}
							/>
						) : null}
						{candidate.expectedSalary?.min > 0 ? (
							<HighlightedCandidateCardInfo
								title="Candidate Expected Salary"
								value={getFormattedSalary(candidate.expectedSalary)}
							/>
						) : null}
						{candidate.noticePeriod ? (
							<HighlightedCandidateCardInfo
								title="Candidate Notice Period"
								value={getNoticePeriod(candidate.noticePeriod)}
							/>
						) : null}
					</>
				) : null}
			</>
		);
	};

	const renderTechnicalReports = (application, candidate) => {
		let technicalReportComp: any = null;
		if (application) {
			if (application.technicalReports?.length) {
				technicalReportComp = application.technicalReports.map(app => (
					<p>
						<b>{props.minimal ? 'Technical Report' : app.name}: </b>
						<a
							href={app.url}
							onClick={e => {
								e.preventDefault();
								setPdfViewerState({
									visible: true,
									title: `${candidate.code}'s Technical Report`,
									url: app.url
								});
							}}
						>
							view
						</a>
					</p>
				));
			}
		} else if (candidate) {
			if (props.minimal) return null;
			technicalReportComp = (
				<p>
					{showCustomReports ? <b>Default Technical Report: </b> : <b>Technical Report: </b>}
					{candidate.technicalReport ? (
						<>
							<a
								href={candidate.technicalReport}
								onClick={e => {
									e.preventDefault();
									setPdfViewerState({
										visible: true,
										title: `${candidate.code}'s Technical Report`,
										url: candidate.technicalReport
									});
								}}
							>
								view
							</a>
						</>
					) : (
						'NA'
					)}
				</p>
			);
			{
				showCustomReports && candidate.customTechnicalReports
					? candidate.customTechnicalReports.map(report => (
							<p key={report.url}>
								<b>{`${report.name} Report: `}</b>

								<a
									href={report.url}
									onClick={e => {
										e.preventDefault();
										setPdfViewerState({
											visible: true,
											title: `${candidate.code}'s ${report.name} Technical Report`,
											url: report.url
										});
									}}
								>
									view
								</a>
							</p>
					  ))
					: null;
			}
		}
		return technicalReportComp;
	};

	const inviteDeveloper = async () => {
		let resp;
		try {
			resp = await candidatesService.inviteDeveloper(candidate.email);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		notifySuccess('Invitation sent successfully!');
		return resp.data;
	};

	const resendDeveloperInvitation = async () => {
		let resp;
		try {
			resp = await candidatesService.resendDeveloperInvitation(candidate.invitation?.id);
		} catch (e: any) {
			notifyError(e.message);
			return;
		}
		notifySuccess('Invitation resent successfully!');
		return resp.data;
	};

	const candidateLocation = props.candidate.city
		? props.candidate.country
			? `${props.candidate.city}, ${props.candidate.country}`
			: props.candidate.city
		: props.candidate.country || 'NA';

	const candidateViewModalFooter = [<Button onClick={props.onCancel}>Cancel</Button>];

	props.minimal
		? candidateViewModalFooter.push(
				<Button onClick={props.onCancel} type="primary">
					Ok
				</Button>
		  )
		: null;
	props.application && !props.minimal
		? candidateViewModalFooter.push(
				<Button type="primary" onClick={() => props.onEdit(application)}>
					Edit Application
				</Button>
		  )
		: null;

	!props.minimal
		? candidateViewModalFooter.push(
				<Button onClick={props.onCancel} type="primary">
					<Link
						to={`/dashboard/candidate/${props.candidate.id}/?source=${getCandidateTypeFromCode(
							props.candidate.code
						)}`}
					>
						View Profile
					</Link>
				</Button>
		  )
		: null;

	const getCandidateCV = async e => {
		e.preventDefault();
		document.addEventListener('contextmenu', event => event.preventDefault());
		document.addEventListener('keydown', function (event) {
			if (event.ctrlKey) {
				event.preventDefault();
			}
		});
		const buffer = await candidatesService.getCandidateCV(candidate.id, getCandidateTypeFromCode(candidate.code));
		if (buffer) {
			var blob = new Blob([buffer], { type: 'octet/stream' });
			var file = new File([blob], 'pdf');
			var url = window.URL.createObjectURL(file) + '#view=FitW';
			setPdfViewerState({
				visible: true,
				title: `${candidate.code}'s CV`,
				url: url
			});
			URL.revokeObjectURL(url);
			blob = new Blob();
		}
	};

	return (
		<Modal
			title={
				<div className="d-flex align-items-center justify-content-between">
					<span>
						{props.minimal
							? `${candidate.code} Profile`
							: `${candidate.firstName} ${candidate.lastName} Profile`}
					</span>

					<Row gutter={24} className="me-2">
						<Col xs={12}>
							{props.copyable && (
								<TAButton
									btntype={ButtonType.GHOST}
									onClick={() => {
										if (copied) return;
										const url = new URL(window.location.toString());
										url.searchParams.append('applicant', application.id);
										navigator.clipboard.writeText(url.toString()).then(() => setCopied(true));
									}}
								>
									<span
										style={{
											display: 'flex',
											alignItems: 'center'
										}}
									>
										<LinkOutlined style={{ fontSize: '18px' }} />
										<span className="p-1">{copied ? 'Link Copied' : 'Copy Link'}</span>
									</span>
								</TAButton>
							)}
						</Col>
						{application ? null : (
							<Col xs={props.copyable ? 12 : 20}>
								{!props.minimal && (props.invitationResendable || !candidate.isRegistered) ? (
									<TAButton
										btntype={ButtonType.GHOST}
										onClick={async () => {
											if (invitationSent) return;
											const invitation = candidate.invitation
												? await resendDeveloperInvitation()
												: await inviteDeveloper();
											if (invitation && props.onCandidateUpdate) {
												setInvitationSent(true);
												props.onCandidateUpdate(
													produce(candidate, draft => {
														draft.invitation = invitation;
													})
												);
											}
										}}
									>
										<span
											style={{
												display: 'flex',
												alignItems: 'center'
											}}
										>
											{candidate.invitation ? (
												<RedoOutlined style={{ fontSize: '18px' }} />
											) : (
												<UserAddOutlined style={{ fontSize: '18px' }} />
											)}
											<span className="p-1">
												{invitationSent
													? 'Invitation Sent'
													: candidate.invitation
													? 'Resend Invitation'
													: 'Invite'}
											</span>
										</span>
									</TAButton>
								) : null}
							</Col>
						)}
					</Row>
				</div>
			}
			visible={props.visible}
			footer={candidateViewModalFooter}
			onCancel={props.onCancel}
			onOk={props.onCancel}
			className="ta-modal"
			width={showComments ? '1200px' : '800px'}
			style={{ top: '70px', left: '30px' }}
		>
			<Row gutter={8}>
				<Col xs={24} lg={showComments ? 12 : 24}>
					<Row gutter={8}>
						<Col xs={10} sm={8}>
							<CandidateAvatar
								showAvatar={showAvatar}
								candidate={candidate}
								size={useMedia('sm') ? 150 : 120}
								shape="square"
								src={props.minimal ? null : candidate.avatar}
								icon={candidate.avatar ? undefined : <UserOutlined />}
								style={{
									backgroundColor: props.avatarBgColor || props.candidate.avatarBgColor
								}}
							>
								<span style={{ mixBlendMode: 'difference', fontSize: '4.5rem' }}>
									{candidate.nameInitials}
								</span>
							</CandidateAvatar>
							{!props.minimal ? (
								<Tooltip
									className="d-flex mt-2"
									title={`Candidate is ${
										candidate.isRegistered ? '' : 'not'
									} registered on developers platform.`}
								>
									<b className="me-2">Registered: </b>

									<CheckCircleFilled
										style={{
											fontSize: '18px',
											color: candidate.isRegistered ? '#52c41a' : '#bfbfbf'
										}}
									/>
								</Tooltip>
							) : null}
							{!candidate.isRegistered && !props.minimal ? (
								<div className="mt-2">
									<b className="me-2">Invitation:</b>
									{candidate.invitation ? (
										<Tag color={InvitationStatusColorMap[candidate.invitation.status]}>
											{titlize(candidate.invitation.status)}
										</Tag>
									) : (
										'Not invited'
									)}
									{candidate.invitation && (
										<div>
											<b>Sent </b>
											<span>{timeAgo(new Date(candidate.invitation.createdAt))}</span> <br />
											{candidate.invitation.status === 'pending' ? (
												<>
													<b>Expires </b>
													<span>{timeAgo(new Date(candidate.invitation.expiresAt))}</span>
												</>
											) : null}
										</div>
									)}
								</div>
							) : null}
						</Col>
						<Col xs={{ span: 13, push: 1 }} md={{ span: 16 }} style={{ height: '100%' }}>
							{props.minimal ? null : (
								<p>
									<b>Name: </b>
									{`${candidate.firstName} ${candidate.lastName}`}
								</p>
							)}
							<p>
								<b>Code: </b>
								{candidate.code}
							</p>
							{props.minimal ? null : (
								<>
									<p>
										<b>Email: </b>
										{candidate.email}
									</p>
									<p>
										<b>Phone: </b>
										{candidate.phoneNumber}
									</p>
								</>
							)}
							<p>
								<b>Title: </b>
								{candidate.title}
							</p>
							{props.minimal ? null : (
								<p>
									<b>Location: </b>
									{candidateLocation}
								</p>
							)}
							{props.minimal ? null : (
								<p>
									<Tooltip title="The cv uploaded by candidate on developers platform">
										<b>Developer CV: </b>
										{candidate.developerCv ? (
											<a
												href={candidate.developerCv}
												onClick={e => {
													e.preventDefault();
													setPdfViewerState({
														visible: true,
														title: `${candidate.code}'s CV`,
														url: candidate.developerCv,
														downloadable: true
													});
												}}
											>
												view
											</a>
										) : (
											'NA'
										)}
									</Tooltip>
								</p>
							)}
							<p>
								<Tooltip title={props.minimal ? undefined : 'The CV uploaded by back office'}>
									<b>CV: </b>
									{candidate.cv ? (
										<a
											href={props.minimal ? candidate.cv : '/#'}
											onClick={
												props.minimal
													? getCandidateCV
													: e => {
															e.preventDefault();
															setPdfViewerState({
																visible: true,
																title: `${candidate.code}'s CV`,
																url: candidate.cv,
																downloadable: false
															});
													  }
											}
										>
											view
										</a>
									) : (
										'NA'
									)}
								</Tooltip>
							</p>
							{props.minimal ? null : (
								<p>
									<b>Experience: </b>
									{candidate.yearsOfExp ? candidate.yearsOfExp + ' years' : 'NA'}
								</p>
							)}
							{props.minimal ? null : candidate.dateOfBirth ? (
								<p>
									<b>Age: </b>
									{dayjs().diff(candidate.dateOfBirth, 'y') + ' years old'}
								</p>
							) : null}
							{!props.minimal && application && application.assignedBy ? (
								<p>
									<b>Assigned by: </b>
									{application.assignedBy.profile.firstName} {application.assignedBy.profile.lastName}
								</p>
							) : null}
							<p>
								{candidate.lastCvUpdate ? (
									<>
										<p>{renderCvLastUpdate(candidate.lastCvUpdate)}</p>
									</>
								) : null}
							</p>

							{renderTechnicalReports(application, candidate)}
							<PDFViewer
								url={pdfViewerState.url}
								title={pdfViewerState.title}
								visible={pdfViewerState.visible}
								downloadable={
									props.minimal &&
									props.application &&
									[JobApplicationState.TO_INTERVIEW, JobApplicationState.OFFER].includes(
										props.application.state
									) &&
									props.job.cvDownloadable
								}
								onCancel={() => {
									setPdfViewerState({
										visible: false,
										title: '',
										url: '',
										downloadable: false
									});
								}}
							/>
							{showStateChangeLog && (
								<>
									<p
										style={{ cursor: 'pointer', color: theme.TA_GREEN }}
										onClick={e => setChangeLogModalVisible(true)}
									>
										view state changelog
									</p>
									<ApplicationStateChangeLogViewModal
										application={application}
										visible={changeLogModalVisible}
										onCancel={() => setChangeLogModalVisible(false)}
									/>
								</>
							)}
							{renderSalaryAndNotice(application, candidate)}
							{showApplicationsHistory && (
								<>
									<p
										style={{ cursor: 'pointer', color: theme.TA_GREEN }}
										onClick={e => setApplicationsModalVisible(true)}
									>
										view applications history
									</p>
									<CandidateApplicationHistoryViewModal
										applications={applications}
										visible={applicationsModalVisible}
										onCancel={() => setApplicationsModalVisible(false)}
									/>
								</>
							)}
						</Col>
					</Row>
					{candidate.labels && (
						<Row className="my-3">
							<Col xs={24}>
								<h6>Lables</h6>
							</Col>
							<Col
								xs={24}
								style={{
									paddingTop: 6,
									display: 'flex',
									justifyContent: 'flex-start',
									flexDirection: 'row',
									flexWrap: 'wrap'
								}}
							>
								{candidate.labels.map(label => (
									<Tag color={label.color} {...label} style={{ marginTop: '4px' }}>
										{label.title}
									</Tag>
								))}
							</Col>
						</Row>
					)}
				</Col>
				{showComments && (
					<Col xs={24} lg={12}>
						<p
							style={{
								fontSize: '15px',
								fontWeight: 700
							}}
						>
							Comments
						</p>
						<div>
							{commentsLoading ? false : <CommentBox commenter={user} onSaveComment={handleAddComment} />}

							{commentsLoading ? (
								<SkeletonList itemsCount={4} />
							) : comments && comments.length ? (
								<div
									ref={commentsContainerRef}
									style={{
										minHeight: '300px',
										maxHeight: '550px',
										overflow: 'auto'
									}}
								>
									{comments.map(comment => (
										<div
											ref={commentFromParam === comment._id ? commentRef : undefined}
											style={{
												paddingTop: '1px',
												paddingBottom: '10px',
												paddingLeft: '5px',
												borderRadius: '5px'
											}}
										>
											<CommentItem
												attachments={comment.attachments}
												comment={comment.content}
												emojis={comment?.emojis}
												commenter={comment.createdBy}
												editable={props.minimal ? comment.createdBy.id === user.userId : true}
												commentDate={comment.createdAt}
												// pRef={commentFromParam === comment._id ? paragrapghRef : undefined}
												// key={comment._id}

												onDelete={() => {
													Modal.confirm({
														title: 'Are you sure you want to delete this comment',
														okButtonProps: { type: 'primary' },
														onOk: async () => {
															const deleted = await jobsService.deleteApplicantComment(
																job._id,
																application.id,
																comment._id
															);
															if (deleted) {
																setComments(comments.filter(c => c._id != comment._id));
															}
														}
													});
												}}
												onEdit={async (val, callback) => {
													const updated = await jobsService.editApplicantComment(
														job._id,
														application.id,
														comment._id,
														val
													);
													if (updated) {
														setComments(
															comments.map(c => {
																if (c._id === comment._id) c.content = val;
																return c;
															})
														);
														callback();
													}
												}}
												onSetEmoji={async val => {
													setCommentsLoading(true);
													await jobsService.setEmoji(comment._id, val);
													jobsService
														.getApplicantComments(job._id, application.id)
														.then(comments => {
															setComments(comments);
														});

													await jobsService.getApplicantComments(job._id, application.id);
													setCommentsLoading(false);
												}}
											/>
										</div>
									))}
								</div>
							) : (
								<p className="mt-3">No comments yet</p>
							)}
						</div>
					</Col>
				)}
			</Row>
		</Modal>
	);
}
